/**
 * 공용 인앱 데이트피커.
 * 브라우저 기본 <input type="date"> 대신 쓰는 팝오버 달력 —
 * 표기는 항상 YYYY-MM-DD 로 고정된다(로케일에 따라 흔들리지 않게).
 * `.att-dp` 래퍼 안의 버튼(.att-dp-btn) + hidden input 페어에 붙이며,
 * 스타일은 layout.css 의 .att-dp-* 클래스를 그대로 쓴다.
 */
let openPop = null;
let openFor = null;

// 요일 줄 기본값은 문서 언어를 따른다 (EN 세션의 CRM/ERP 피커가 한국어 요일을 보이던 문제, QA ux-19)
function defaultWeekdays() {
    const lang = (document.documentElement.lang || 'ko').toLowerCase();
    return lang.startsWith('en') ? 'Su,Mo,Tu,We,Th,Fr,Sa' : '일,월,화,수,목,금,토';
}
function navLabel(next) {
    const en = (document.documentElement.lang || 'ko').toLowerCase().startsWith('en');
    return next ? (en ? 'Next month' : '다음 달') : (en ? 'Previous month' : '이전 달');
}

function fmt(y, m, d) {
    return y + '-' + String(m + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
}

export function closeDatePicker() {
    if (openPop) {
        openPop.remove();
        openPop = null;
        openFor = null;
    }
}

/** 버튼 라벨을 hidden 값(YYYY-MM-DD)과 동기화한다. */
export function syncDateLabel(btn, hidden, placeholder) {
    const label = btn.querySelector('.att-dp-label');
    if (!label) return;
    if (hidden.value) {
        label.textContent = hidden.value;
        label.classList.remove('text-muted');
    } else {
        label.textContent = placeholder || '날짜 선택';
        label.classList.add('text-muted');
    }
}

/**
 * datePickerLayout(view, weekdaysCsv, weekStart) — the month view's first cell (a Date) and the
 * head labels in column order. weekStart 'mon' (or 1) starts the week on Monday — the calendar
 * panel passes the viewer's 주 시작 요일 (XA21); anything else keeps Sunday.
 */
export function datePickerLayout(view, weekdaysCsv, weekStart) {
    const first = weekStart === 'mon' || weekStart === 1 || weekStart === '1' ? 1 : 0;
    const names = String(weekdaysCsv || '').split(',');
    const weekdays = names.length === 7 ? names.slice(first).concat(names.slice(0, first)) : names;
    const back = (view.getDay() - first + 7) % 7;
    return { start: new Date(view.getFullYear(), view.getMonth(), 1 - back), weekdays };
}

/**
 * opts: { weekdays: '일,월,…', weekStart: 'sun'|'mon', placeholder, onOpen(), onSet() }
 * onOpen 은 다른 팝오버를 닫는 용도, onSet 은 값 확정 후 후처리.
 */
export function attachDatePicker(btn, hidden, opts) {
    const o = opts || {};
    btn.addEventListener('click', () => {
        if (openFor === btn) {
            closeDatePicker();
            return;
        }
        closeDatePicker();
        if (o.onOpen) o.onOpen();

        const pop = document.createElement('div');
        pop.className = 'att-dp-pop';
        let view = hidden.value ? new Date(hidden.value + 'T00:00:00') : new Date();
        view = new Date(view.getFullYear(), view.getMonth(), 1);

        function render() {
            pop.innerHTML = '';
            const head = document.createElement('div');
            head.className = 'att-dp-head';
            const prev = document.createElement('button');
            prev.type = 'button';
            prev.className = 'att-dp-nav';
            prev.textContent = '‹';
            prev.setAttribute('aria-label', navLabel(false)); prev.title = navLabel(false);
            const label = document.createElement('strong');
            label.textContent = view.getFullYear() + '. ' + String(view.getMonth() + 1).padStart(2, '0');
            const next = document.createElement('button');
            next.type = 'button';
            next.className = 'att-dp-nav';
            next.textContent = '›';
            next.setAttribute('aria-label', navLabel(true)); next.title = navLabel(true);
            prev.addEventListener('click', () => {
                view = new Date(view.getFullYear(), view.getMonth() - 1, 1);
                render();
            });
            next.addEventListener('click', () => {
                view = new Date(view.getFullYear(), view.getMonth() + 1, 1);
                render();
            });
            head.appendChild(prev);
            head.appendChild(label);
            head.appendChild(next);
            pop.appendChild(head);

            const grid = document.createElement('div');
            grid.className = 'att-dp-grid';
            const layout = datePickerLayout(view, o.weekdays || defaultWeekdays(), o.weekStart);
            layout.weekdays.forEach((w) => {
                const wd = document.createElement('span');
                wd.className = 'att-dp-wd';
                wd.textContent = w;
                grid.appendChild(wd);
            });
            const today = new Date();
            const todayISO = fmt(today.getFullYear(), today.getMonth(), today.getDate());
            const start = layout.start;
            for (let i = 0; i < 42; i += 1) {
                const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
                const iso = fmt(d.getFullYear(), d.getMonth(), d.getDate());
                const cell = document.createElement('button');
                cell.type = 'button';
                cell.className = 'att-dp-day';
                cell.textContent = d.getDate();
                if (d.getMonth() !== view.getMonth()) cell.classList.add('dim');
                if (d.getDay() === 0) cell.classList.add('sun');
                if (d.getDay() === 6) cell.classList.add('sat');
                if (iso === todayISO) cell.classList.add('today');
                if (hidden.value === iso) cell.classList.add('sel');
                cell.addEventListener('click', () => {
                    hidden.value = iso;
                    syncDateLabel(btn, hidden, o.placeholder);
                    closeDatePicker();
                    if (o.onSet) o.onSet(iso);
                });
                grid.appendChild(cell);
            }
            pop.appendChild(grid);
        }
        render();
        if (o.clearLabel) {
            // 날짜 지우기 (미정)
            const clear = document.createElement('button');
            clear.type = 'button';
            clear.className = 'att-dp-clear';
            clear.textContent = o.clearLabel;
            clear.addEventListener('click', () => {
                hidden.value = '';
                syncDateLabel(btn, hidden, o.placeholder);
                closeDatePicker();
                if (o.onSet) o.onSet('');
            });
            pop.appendChild(clear);
        }
        btn.parentElement.appendChild(pop);
        openPop = pop;
        openFor = btn;
    });
}

if (typeof document !== 'undefined') document.addEventListener('click', (e) => {
    // ‹ › 는 render() 로 팝 안을 다시 그려 누른 단추가 문서에서 떨어진다 — 떨어진 대상은 "바깥"이 아니다
    // (예전에는 달을 넘기는 순간 달력이 닫혔다; BJ 라운드에서 기간 팝오버 e2e 가 잡았다).
    if (!e.target.isConnected) return;
    if (openPop && !openPop.contains(e.target) && !e.target.closest('.att-dp-btn')) closeDatePicker();
});

export function isDatePickerOpen() {
    return !!openPop;
}
